<template>
  <v-data-table
    :headers="headers"
    :items="users"
    sort-by="calories"
    class="elevation-1"
    :search="search"
    :loading="loadingTable"
    :loading-text="loadingText"
  >
    <template v-slot:[`item.status`]="{ item }">
      <v-chip :color="item.enabled ? 'success' : 'warning'">
        {{ item.status }}
      </v-chip>
    </template>

    <template v-slot:top>
      <div class="tw-px-2 md:tw-px-6 tw-mb-4">
        <div class="tw-flex tw-justify-end">
          <div class="tw-w-full sm:tw-w-1/2 md:tw-w-1/3">
            <v-text-field
              v-model="search"
              append-icon="mdi-magnify"
              label="Search users"
              placeholder="Search by name, email, department, role..."
              single-line
              hide-details
              clearable
              outlined
              dense
            ></v-text-field>
          </div>
        </div>
      </div>

      <v-dialog v-model="dialog" max-width="800px">
        <v-card class="dlg-card">
          <dialog-header
            :title="formTitle"
            icon="mdi-account-edit"
            @close="close"
          />
          <v-divider></v-divider>

          <v-card-text class="pt-6 pb-3 dlg-body">
            <v-container>
              <v-row>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="editedItem.name"
                    prepend-inner-icon="mdi-account"
                    label="Name"
                    outlined
                    dense
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="editedItem.email_address"
                    prepend-inner-icon="mdi-email"
                    label="Email"
                    outlined
                    dense
                    disabled
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="editedItem.department"
                    prepend-inner-icon="mdi-domain"
                    label="Department"
                    outlined
                    dense
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-select
                    v-model="editedItem.role"
                    :items="roles"
                    item-text="name"
                    item-value="value"
                    label="Role"
                    prepend-inner-icon="mdi-shield-account"
                    outlined
                    dense
                  ></v-select>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="editedItem.region"
                    prepend-inner-icon="mdi-map-marker"
                    label="Region"
                    outlined
                    dense
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="editedItem.extension"
                    prepend-inner-icon="mdi-phone"
                    label="Extension No."
                    outlined
                    dense
                  ></v-text-field>
                </v-col>
              </v-row>
            </v-container>
          </v-card-text>
          <v-divider></v-divider>
          <v-card-actions class="pa-4 dlg-actions">
            <v-spacer></v-spacer>
            <v-btn text color="error" @click="close" class="px-4">
              <v-icon left small>mdi-close</v-icon>
              Cancel
            </v-btn>
            <v-btn
              color="secondary"
              @click="save"
              :loading="saving"
              class="px-6"
              elevation="2"
            >
              <v-icon left small>mdi-content-save</v-icon>
              Save
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Delete Confirmation Dialog -->
      <v-dialog v-model="deleteDialog" max-width="500px">
        <v-card class="dlg-card">
          <dialog-header
            title="Confirm Deletion"
            icon="mdi-trash-can-outline"
            @close="closeDelete"
          />
          <v-divider></v-divider>
          <v-card-text class="dlg-body">
            Are you sure you want to delete user
            <strong>{{ deleteItem ? deleteItem.name : '' }}</strong
            >? This action cannot be undone.
          </v-card-text>
          <v-card-actions class="dlg-actions">
            <v-spacer></v-spacer>
            <v-btn color="grey darken-1" text @click="closeDelete"
              >Cancel</v-btn
            >
            <v-btn
              color="red darken-1"
              text
              @click="deleteConfirm"
              :loading="deleting"
            >
              Delete
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </template>
    <template v-slot:[`item.actions`]="{ item }">
      <v-icon small class="mr-2" color="secondary" @click="editItem(item)">
        mdi-pencil
      </v-icon>
      <v-icon
        v-if="!isCurrentUser(item)"
        small
        color="error"
        @click="deleteItemConfirm(item)"
      >
        mdi-delete
      </v-icon>
    </template>
  </v-data-table>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import DialogHeader from '@/components/DialogHeader'
export default {
  components: { DialogHeader },
  data: () => ({
    dialog: false,
    search: '',
    headers: [
      {
        text: 'Id',
        align: 'start',
        sortable: true,
        value: 'id',
      },
      {
        text: 'Name',
        align: 'start',
        sortable: true,
        value: 'name',
      },
      { text: 'Email', value: 'email_address' },
      { text: 'Department', value: 'department' },
      { text: 'Role', value: 'role' },
      { text: 'Region', value: 'region' },
      { text: 'Extension No.', value: 'extension' },
      { text: 'Status', value: 'status' },
      { text: 'Actions', value: 'actions', sortable: false },
    ],
    editedIndex: -1,
    editedItem: {
      id: '',
      name: '',
      email_address: '',
      department: '',
      role: '',
      extension: '',
      region: '',
    },
    defaultItem: {
      id: '',
      name: '',
      email_address: '',
      department: '',
      role: '',
      extension: '',
      region: '',
    },
    deleteDialog: false,
    deleteItem: null,
    deleting: false,
    saving: false,
  }),

  computed: {
    formTitle() {
      return this.editedIndex === -1 ? 'New Item' : 'Edit Item'
    },
    ...mapState(['users', 'loadingTable', 'roles']),
    ...mapGetters(['loadingText']),
  },

  watch: {
    dialog(val) {
      val || this.close()
    },
  },

  mounted() {
    this.$store.dispatch('fetchUser')
    this.$store.dispatch('fetchRoles')
  },
  created() {
    this.$store.state.loadingTable = true
    this.$store.state.loadingText = 'Loading... Please wait'
  },

  methods: {
    editItem(item) {
      this.editedIndex = this.users.indexOf(item)
      this.editedItem = { ...item }
      this.dialog = true
    },

    deleteItemConfirm(item) {
      this.deleteItem = item
      this.deleteDialog = true
    },

    async deleteConfirm() {
      if (!this.deleteItem) return

      this.deleting = true
      try {
        await this.$store.dispatch('deleteUser', this.deleteItem.username)
        this.closeDelete()
      } catch (error) {
        // Error is handled by snackbar in deleteUser action
      } finally {
        this.deleting = false
      }
    },

    closeDelete() {
      this.deleteDialog = false
      this.$nextTick(() => {
        this.deleteItem = null
      })
    },

    close() {
      this.dialog = false
      this.$nextTick(() => {
        this.editedItem = { ...this.defaultItem }
        this.editedIndex = -1
      })
    },

    async save() {
      if (!this.editedItem.username) {
        this.$toasted.error('Cannot save: missing username', { duration: 3000 })
        return
      }

      this.saving = true
      try {
        await this.$store.dispatch('updateUser', {
          username: this.editedItem.username,
          userData: {
            name: this.editedItem.name,
            email_address: this.editedItem.email_address,
            department: this.editedItem.department,
            role: this.editedItem.role,
            extension: this.editedItem.extension,
            region: this.editedItem.region,
          },
        })
        this.close()
      } catch (error) {
        // Error is handled by snackbar in updateUser action
      } finally {
        this.saving = false
      }
    },

    isCurrentUser(user) {
      const currentUser = this.$store.getters['auth/authenticatedUser']
      if (!currentUser) return false

      // Compare by email or username
      return (
        user.email_address === currentUser.email ||
        user.username === currentUser.sub ||
        user.email_address === currentUser['email']
      )
    },
  },
}
</script>
